import type {
  AuditCase,
  AuditMessage,
  AuditSearchHit,
  AuditCaseRequestInput,
  AuditGrant,
  AuditLogEntry,
  AuditChainVerification,
  AuditMe,
  AuditSettings,
  AuditTotpSetup,
} from '@avslack/shared';
import { apiFetch } from '../lib/api';
import { apiBase } from '../lib/runtime';

/**
 * Audit-kereses - a kliens oldali hivasok.
 *
 * Lasd docs/18-audit-kereses.html. Minden itt levo muvelet a szerver
 * kitorolhetetlen betekintesi naplojaba kerul.
 */

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  body: JSON.stringify(body),
});

export const auditMeKey = ['audit', 'me'] as const;

export const getAuditMe = () => apiFetch<AuditMe>('/api/audit/me');

export const getAuditSettings = () =>
  apiFetch<{ settings: AuditSettings }>('/api/audit/settings').then((r) => r.settings);

export const saveAuditSettings = (settings: AuditSettings) =>
  apiFetch<{ settings: AuditSettings }>('/api/audit/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  }).then((r) => r.settings);

export const startAuditTotp = () => apiFetch<AuditTotpSetup>('/api/audit/totp/setup', json({}));

export const confirmAuditTotp = (code: string) =>
  apiFetch<{ ok: boolean }>('/api/audit/totp/confirm', json({ code }));

/** Elveszett telefon: a szuperadmin visszaallitja, a kolléga ujra beallitja. */
export const resetAuditTotp = (userId: string) =>
  apiFetch<{ ok: boolean }>(`/api/audit/totp/${userId}`, { method: 'DELETE' });

export const verifyAuditChain = () => apiFetch<AuditChainVerification>('/api/audit/log/verify');

export const listAuditGrants = () =>
  apiFetch<{ grants: AuditGrant[] }>('/api/audit/grants').then((r) => r.grants);

export const grantAudit = (userId: string, days: number) =>
  apiFetch<{ grants: AuditGrant[] }>('/api/audit/grants', json({ userId, days })).then(
    (r) => r.grants,
  );

export const revokeAudit = (userId: string) =>
  apiFetch<{ grants: AuditGrant[] }>(`/api/audit/grants/${userId}`, { method: 'DELETE' }).then(
    (r) => r.grants,
  );

export interface AuditDirectory {
  channels: { id: string; name: string; kind: string; isArchived: boolean }[];
  users: { id: string; name: string }[];
}

export const getAuditDirectory = () => apiFetch<AuditDirectory>('/api/audit/directory');

export const listAuditCases = () =>
  apiFetch<{ cases: AuditCase[] }>('/api/audit/cases').then((r) => r.cases);

export const requestAuditCase = (input: AuditCaseRequestInput) =>
  apiFetch<{ case: AuditCase }>('/api/audit/cases', json(input)).then((r) => r.case);

export const decideAuditCase = (
  id: string,
  input: { approve: boolean; note?: string; password: string; code: string },
) =>
  apiFetch<{ case: AuditCase }>(`/api/audit/cases/${id}/decision`, json(input)).then((r) => r.case);

export const closeAuditCase = (id: string) =>
  apiFetch<{ case: AuditCase }>(`/api/audit/cases/${id}/close`, json({})).then((r) => r.case);

export const auditExportUrl = (id: string, format: 'html' | 'json') =>
  `${apiBase()}/api/audit/cases/${id}/export?format=${format}`;

/**
 * Kereses az ugyben. MINDEN hivas naplozott betekintes - ezert a felulet csak
 * kifejezett kuldesre keres, nem gepeles kozben.
 */
export const searchAuditCase = (id: string, q: string, offset = 0) =>
  apiFetch<{ hits: AuditSearchHit[]; hasMore: boolean }>(
    `/api/audit/cases/${id}/search?q=${encodeURIComponent(q)}&offset=${offset}`,
  );

/** Melleklet megnyitasa az ugyben - naplozott, uj fulon. */
export const auditFileUrl = (id: string, fileId: string) =>
  `${apiBase()}/api/audit/cases/${id}/files/${fileId}`;

export const auditCaseContext = (id: string, messageId: string) =>
  apiFetch<{ messages: AuditMessage[] }>(
    `/api/audit/cases/${id}/messages/${messageId}/context`,
  ).then((r) => r.messages);

export interface AuditLogQuery {
  actorId?: string;
  caseId?: string;
  action?: string;
  subjectId?: string;
  q?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export const listAuditLog = (query: AuditLogQuery) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  return apiFetch<{ entries: AuditLogEntry[] }>(`/api/audit/log?${params.toString()}`).then(
    (r) => r.entries,
  );
};
